/**
 * EmailInbox
 *
 * Shared business email inbox view. Lives inside the Messages area as a sibling
 * to the in-app chat. Displays threads, message bodies fetched live, and supports:
 *   - Reply (sent via connected mailbox provider)
 *   - "Add as couple/enquiry" for unknown senders
 *   - "Save this message as a CRM note" for known leads (or pick a lead)
 *
 * Email bodies are NOT persisted server-side. They are fetched live each time
 * a thread is opened.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Chip,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Link as LinkIcon } from '@mui/icons-material';
import {
  EnvelopeSimple,
  ArrowsClockwise,
  UserCirclePlus,
  NotePencil,
  Paperclip,
  Gear
} from '@phosphor-icons/react';
import { useBusiness } from '../../context/BusinessContext';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import MailConnectionSettings from '../Supplier/MailConnection/MailConnectionSettings';
import { chatTheme } from '../../theme/chatTheme';

const HEADER_HEIGHT = 56;

function extractEmail(headerValue = '') {
  const match = String(headerValue).match(/<([^>]+)>/);
  if (match) return match[1].trim().toLowerCase();
  return String(headerValue).trim().toLowerCase();
}

function extractName(headerValue = '') {
  const match = String(headerValue).match(/^(.+?)\s*<[^>]+>$/);
  if (match) return match[1].replace(/"/g, '').trim();
  return '';
}

function formatRelative(value) {
  if (!value) return '';
  try {
    const date = new Date(value);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h`;
    if (date.toDateString() === new Date(now - 24 * 60 * 60 * 1000).toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch (_) {
    return '';
  }
}

function trimQuotedText(content = '') {
  const text = String(content || '');
  if (!text.trim()) return text;

  const markers = [
    /\n[-]{2,}\s*Original Message\s*[-]{2,}/i,
    /\nFrom:\s.*\nSent:\s.*\nTo:\s.*\nSubject:\s.*/i,
    /\nOn .+ wrote:/i
  ];

  let cutIndex = -1;
  for (const marker of markers) {
    const match = text.match(marker);
    if (match?.index !== undefined) {
      if (cutIndex === -1 || match.index < cutIndex) cutIndex = match.index;
    }
  }

  if (cutIndex === -1) return text;
  return text.slice(0, cutIndex).trim();
}

function trimQuotedHtml(content = '') {
  const html = String(content || '');
  if (!html.trim()) return html;

  let out = html;

  // Common quoted blocks added by webmail clients.
  out = out.replace(/<blockquote[\s\S]*$/i, '');
  out = out.replace(/<div[^>]*class=["'][^"']*(gmail_quote|yahoo_quoted|protonmail_quote)[^"']*["'][\s\S]*$/i, '');

  // Outlook-style separator then historical thread.
  out = out.replace(/<hr[^>]*>[\s\S]*$/i, '');

  return out.trim();
}

function ThreadListItem({ thread, active, onClick }) {
  const name = extractName(thread.from) || extractEmail(thread.from);
  return (
    <ListItemButton
      selected={active}
      onClick={onClick}
      sx={{
        alignItems: 'flex-start',
        ...chatTheme.sidebar.conversationItem,
        backgroundColor: active ? 'rgba(239, 65, 146, 0.08)' : 'transparent'
      }}
    >
      <ListItemText
        primaryTypographyProps={{ noWrap: true, sx: { fontWeight: thread.unread ? 700 : 500 } }}
        secondaryTypographyProps={{ component: 'div' }}
        primary={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {name || extractEmail(thread.from) || '(unknown sender)'}
            </Box>
            {thread.lead && (
              <Tooltip title={`Linked to ${thread.lead.name || thread.lead.email}`}>
                <Box sx={{ display: 'inline-flex', color: 'success.main', flexShrink: 0 }}>
                  <LinkIcon sx={{ fontSize: 14 }} />
                </Box>
              </Tooltip>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {formatRelative(thread.receivedAt)}
            </Typography>
          </Stack>
        }
        secondary={
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: thread.unread ? 600 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {thread.subject || '(no subject)'}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {thread.snippet || ''}
            </Typography>
          </Box>
        }
      />
    </ListItemButton>
  );
}

function ThreadDetail({
  thread,
  detail,
  loading,
  onReply,
  sending,
  checkingLeadMatch,
  onAddAsLead,
  onSaveNote,
  matchedLead,
  connectionEmail,
  onViewLead
}) {
  const [replyBody, setReplyBody] = useState('');

  useEffect(() => {
    setReplyBody('');
  }, [thread?.threadId]);

  if (!thread) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          p: 4
        }}
      >
        <Typography>Select an email thread to read it</Typography>
      </Box>
    );
  }

  const senderEmail = extractEmail(thread.from);
  const subject = detail?.messages?.[0]?.subject || thread.subject || '(no subject)';
  const displayedMessages = [...(detail?.messages || [])].sort((a, b) => {
    const da = a?.receivedAt ? new Date(a.receivedAt).getTime() : 0;
    const db = b?.receivedAt ? new Date(b.receivedAt).getTime() : 0;
    return db - da;
  });

  const handleReply = () => {
    if (!replyBody.trim()) return;
    const lastMessage = detail?.messages?.[detail.messages.length - 1];
    onReply({
      to: senderEmail ? [senderEmail] : [],
      subject: subject.toLowerCase().startsWith('re:') ? subject : `Re: ${subject}`,
      body: replyBody,
      inReplyTo: lastMessage?.internetMessageId || undefined,
      references: lastMessage?.references || lastMessage?.internetMessageId || undefined,
      replyToMessageId: lastMessage?.messageId
    });
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          minHeight: HEADER_HEIGHT,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          px: 2.5,
          py: 1.5,
          gap: 1,
          flexShrink: 0
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, mb: 0.25 }}>
            {subject}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            From {thread.from}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {matchedLead ? (
            <>
              <Tooltip title="Save as a note on their record">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<NotePencil size={16} />}
                  onClick={onSaveNote}
                >
                  Save as note
                </Button>
              </Tooltip>
              <Button
                size="small"
                variant="contained"
                onClick={onViewLead}
                sx={{
                  backgroundColor: '#111111',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#000000' }
                }}
              >
                View in My Couples
              </Button>
            </>
          ) : (
            <Tooltip title={senderEmail ? `Add ${senderEmail} to My Couples` : 'Add to My Couples'}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<UserCirclePlus size={16} />}
                  onClick={onAddAsLead}
                  disabled={!senderEmail || checkingLeadMatch}
                  sx={{
                    backgroundColor: '#111111',
                    color: '#ffffff',
                    '&:hover': { backgroundColor: '#000000' }
                  }}
                >
                  {checkingLeadMatch ? 'Checking…' : 'Add to My Couples'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Box sx={{ ...chatTheme.messageList, backgroundColor: '#f6f6f6' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && displayedMessages.length === 0 && (
          <Alert severity="info">This thread has no readable messages.</Alert>
        )}
        {!loading && displayedMessages.map((msg) => {
          const isOutgoing = Boolean(
            connectionEmail
            && extractEmail(msg.from || '') === String(connectionEmail).toLowerCase()
          );

          const bodyTextToRender = isOutgoing ? trimQuotedText(msg.bodyText || msg.snippet || '') : (msg.bodyText || msg.snippet || '');
          const bodyHtmlToRender = isOutgoing ? trimQuotedHtml(msg.bodyHtml || '') : (msg.bodyHtml || '');

          return (
            <Box
              key={msg.messageId}
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                border: '1px solid',
                borderColor: isOutgoing ? 'rgba(239, 65, 146, 0.35)' : 'rgba(0, 0, 0, 0.08)',
                p: 2,
                mb: 1
              }}
            >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{msg.from || '(unknown)'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {msg.to ? `to ${msg.to}` : ''}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={isOutgoing ? 'Your reply' : 'Incoming'}
                color={isOutgoing ? 'primary' : 'default'}
                variant={isOutgoing ? 'filled' : 'outlined'}
              />
              <Typography variant="caption" color="text.secondary">
                {msg.receivedAt ? new Date(msg.receivedAt).toLocaleString('en-GB') : ''}
              </Typography>
            </Stack>

            {bodyHtmlToRender ? (
              <Box
                sx={{
                  '& a': { color: 'primary.main' },
                  '& img': { maxWidth: '100%' },
                  fontSize: 14
                }}
                // Bodies are fetched live from the provider; sanitisation should be added before
                // expanding this to richer content. Plain text is preferred when available.
                dangerouslySetInnerHTML={{ __html: bodyHtmlToRender }}
              />
            ) : (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {bodyTextToRender}
              </Typography>
            )}

            {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Paperclip size={14} /> {msg.attachments.length} attachment{msg.attachments.length !== 1 ? 's' : ''}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                  {msg.attachments.map((att) => (
                    <Chip
                      key={att.attachmentId}
                      size="small"
                      label={att.filename || 'attachment'}
                      variant="outlined"
                    />
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Open attachments in your usual email app. We do not save them inside Idotoo.
                </Typography>
              </Box>
            )}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ ...chatTheme.messageInput.container, flexShrink: 0 }}>
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          placeholder={`Reply to ${senderEmail || 'sender'}…`}
          value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
          sx={chatTheme.messageInput.input}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
            Replies go from your enquiries address.
          </Typography>
          <Button onClick={() => setReplyBody('')} disabled={sending || !replyBody}>Clear</Button>
          <Button
            variant="contained"
            onClick={handleReply}
            disabled={sending || !replyBody.trim() || !senderEmail}
            sx={{ borderRadius: '10px', px: 2.5, minWidth: 130 }}
          >
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

function AddLeadDialog({ open, onClose, onSubmit, defaultEmail, defaultName, submitting }) {
  const [name, setName] = useState(defaultName || '');
  const [email, setEmail] = useState(defaultEmail || '');

  useEffect(() => {
    if (open) {
      setName(defaultName || '');
      setEmail(defaultEmail || '');
    }
  }, [open, defaultName, defaultEmail]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add to My Couples</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
        />
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={submitting || !name.trim() || !email.trim()}
          onClick={() => onSubmit({ name: name.trim(), email: email.trim() })}
          sx={{
            backgroundColor: '#111111',
            color: '#ffffff',
            '&:hover': { backgroundColor: '#000000' }
          }}
        >
          {submitting ? 'Adding…' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SaveNoteDialog({ open, onClose, onSubmit, defaultTitle, defaultContent, submitting }) {
  const [title, setTitle] = useState(defaultTitle || '');
  const [content, setContent] = useState(defaultContent || '');
  const [extra, setExtra] = useState('');

  useEffect(() => {
    if (open) {
      setTitle(defaultTitle || '');
      setContent(defaultContent || '');
      setExtra('');
    }
  }, [open, defaultTitle, defaultContent]);

  const fullContent = extra.trim() ? `${extra.trim()}\n\n— Email content —\n${content}` : content;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save as a couple note</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Note title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
        />
        <TextField
          fullWidth
          label="Extra context (optional)"
          placeholder="Add anything important for the team here. It will appear above the email content in the note."
          multiline
          minRows={2}
          value={extra}
          onChange={(e) => setExtra(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Email content (preview)"
          multiline
          minRows={5}
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={submitting || !title.trim() || !content.trim()}
          onClick={() => onSubmit({ title: title.trim(), content: fullContent })}
        >
          {submitting ? 'Saving…' : 'Save note'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const EmailInbox = ({ onUnreadCountChange }) => {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.business?._id;
  const navigate = useNavigate();
  const location = useLocation();

  const [connection, setConnection] = useState(null);
  const [connLoading, setConnLoading] = useState(true);
  const [manageOpen, setManageOpen] = useState(false);
  const [callbackBanner, setCallbackBanner] = useState(null);

  const [threads, setThreads] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState('');

  const [activeThreadId, setActiveThreadId] = useState(null);
  const [activeDetail, setActiveDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [checkingLeadMatch, setCheckingLeadMatch] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [saveNoteOpen, setSaveNoteOpen] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  const activeThread = useMemo(() => threads.find((t) => t.threadId === activeThreadId) || null, [threads, activeThreadId]);
  const matchedLead = activeThread?.lead || activeDetail?.lead || null;

  useEffect(() => {
    if (typeof onUnreadCountChange === 'function') {
      const unreadCount = threads.reduce((sum, thread) => sum + (thread.unread ? 1 : 0), 0);
      onUnreadCountChange(unreadCount);
    }
  }, [threads, onUnreadCountChange]);

  const refreshConnection = useCallback(async () => {
    if (!businessId) return null;
    setConnLoading(true);
    try {
      const response = await api.get('/api/mail-sync/status', { params: { businessId } });
      setConnection(response.data.connection || null);
      return response.data.connection || null;
    } catch (_) {
      setConnection(null);
      return null;
    } finally {
      setConnLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    refreshConnection();
  }, [refreshConnection]);

  // Handle OAuth callback redirect querystring; show inline banner and clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mailSync = params.get('mailSync');
    if (!mailSync) return;

    if (mailSync === 'connected') {
      setCallbackBanner({ severity: 'success', text: `Connected ${params.get('email') || 'mailbox'} successfully.` });
    } else if (mailSync === 'error') {
      setCallbackBanner({ severity: 'error', text: `Could not connect mailbox: ${params.get('reason') || 'unknown error'}` });
    }

    params.delete('mailSync');
    params.delete('email');
    params.delete('reason');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const fetchThreads = useCallback(async ({ reset = true, pageToken = null } = {}) => {
    if (!businessId) return;
    if (reset) {
      setListLoading(true);
      setListError('');
    } else {
      setLoadingMore(true);
    }
    try {
      const response = await api.get('/api/mail-sync/threads', {
        params: {
          businessId,
          ...(pageToken ? { pageToken } : {})
        }
      });
      const fetchedThreads = response.data.threads || [];

      if (reset) {
        const initialThreads = fetchedThreads.length > 0
          ? fetchedThreads.map((thread, index) => (index === 0 ? { ...thread, unread: false } : thread))
          : fetchedThreads;
        setThreads(initialThreads);
        setNextPageToken(response.data.nextPageToken || null);

        // Always preload the first thread when inbox data loads.
        if (fetchedThreads.length > 0) {
          const firstThreadId = fetchedThreads[0].threadId;
          setActiveThreadId(firstThreadId);
          const detailResponse = await api.get(`/api/mail-sync/threads/${encodeURIComponent(firstThreadId)}`, {
            params: { businessId }
          });
          setActiveDetail(detailResponse.data);
          // Sync read status with provider so unread state stays consistent across reloads.
          await api.post(`/api/mail-sync/threads/${encodeURIComponent(firstThreadId)}/read`, { businessId })
            .catch(() => {});
        } else {
          setActiveThreadId(null);
          setActiveDetail(null);
        }
      } else {
        setThreads((prev) => {
          const merged = [...prev];
          const existing = new Set(prev.map((thread) => thread.threadId));
          for (const thread of fetchedThreads) {
            if (!existing.has(thread.threadId)) {
              merged.push(thread);
            }
          }
          return merged;
        });
        setNextPageToken(response.data.nextPageToken || null);
      }
    } catch (err) {
      setListError(err?.response?.data?.message || 'Failed to load inbox');
    } finally {
      if (reset) setListLoading(false);
      else setLoadingMore(false);
    }
  }, [businessId]);

  useEffect(() => {
    if (connection && connection.status === 'active') fetchThreads({ reset: true });
  }, [connection, fetchThreads]);

  const handleSelectThread = useCallback(async (threadId) => {
    setActiveThreadId(threadId);
    setActiveDetail(null);
    setActionError('');
    setActionSuccess('');
    if (!threadId) return;
    setThreads((prev) => prev.map((thread) => (
      thread.threadId === threadId ? { ...thread, unread: false } : thread
    )));
    setDetailLoading(true);
    try {
      const response = await api.get(`/api/mail-sync/threads/${encodeURIComponent(threadId)}`, {
        params: { businessId }
      });
      setActiveDetail(response.data);
      await api.post(`/api/mail-sync/threads/${encodeURIComponent(threadId)}/read`, { businessId })
        .catch(() => {});
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to load email');
    } finally {
      setDetailLoading(false);
    }
  }, [businessId]);

  const handleReply = useCallback(async (payload) => {
    if (!activeThreadId) return;
    setSending(true);
    setActionError('');
    setActionSuccess('');
    try {
      await api.post(`/api/mail-sync/threads/${encodeURIComponent(activeThreadId)}/reply`, {
        businessId,
        ...payload
      });
      setActionSuccess('Reply sent.');
      fetchThreads({ reset: true });
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  }, [activeThreadId, businessId, fetchThreads]);

  const handleSubmitAddLead = useCallback(async ({ name, email }) => {
    setSavingLead(true);
    setActionError('');
    setActionSuccess('');
    try {
      const response = await api.post('/api/leads', {
        businessId,
        name,
        email,
        leadSource: 'from_email'
      });
      const leadId = response.data?._id;
      setActionSuccess('Added to My Couples.');
      setAddLeadOpen(false);

      setThreads((prev) => prev.map((t) => (
        t.threadId === activeThreadId ? { ...t, lead: { _id: leadId, name, email } } : t
      )));
      setActiveDetail((prev) => (prev ? { ...prev, lead: { _id: leadId, name, email } } : prev));
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to add as couple');
    } finally {
      setSavingLead(false);
    }
  }, [businessId, activeThreadId]);

  const handleAddAsLead = useCallback(async () => {
    const senderEmail = extractEmail(activeThread?.from);
    if (!senderEmail) {
      setActionError('Could not identify the sender email for this thread.');
      return;
    }

    setCheckingLeadMatch(true);
    setActionError('');
    setActionSuccess('');

    try {
      const response = await api.get('/api/leads/lookup-email', {
        params: { businessId, email: senderEmail }
      });

      const existingLead = response?.data?.lead;
      if (existingLead?._id) {
        const leadRef = {
          _id: existingLead._id,
          name: existingLead.name,
          email: existingLead.email
        };

        setThreads((prev) => prev.map((t) => (
          t.threadId === activeThreadId ? { ...t, lead: leadRef } : t
        )));
        setActiveDetail((prev) => (prev ? { ...prev, lead: leadRef } : prev));
        setActionSuccess(`Already in My Couples as ${existingLead.name || existingLead.email}.`);
        return;
      }

      setAddLeadOpen(true);
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to check My Couples for this sender');
    } finally {
      setCheckingLeadMatch(false);
    }
  }, [activeThread, activeThreadId, businessId]);

  const handleSubmitSaveNote = useCallback(async ({ title, content }) => {
    if (!matchedLead?._id) {
      setActionError('Link this email to a couple first');
      return;
    }
    const lastMessage = activeDetail?.messages?.[activeDetail.messages.length - 1];
    setSavingNote(true);
    setActionError('');
    setActionSuccess('');
    try {
      await api.post(`/api/mail-sync/messages/${encodeURIComponent(lastMessage?.messageId || 'unknown')}/save-note`, {
        businessId,
        leadId: matchedLead._id,
        title,
        content,
        subject: lastMessage?.subject || activeThread?.subject,
        fromHeader: lastMessage?.from || activeThread?.from
      });
      setActionSuccess('Saved to their notes.');
      setSaveNoteOpen(false);
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to save note');
    } finally {
      setSavingNote(false);
    }
  }, [activeDetail, activeThread, businessId, matchedLead]);

  if (connLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const canManage = ['owner', 'admin'].includes(selectedBusiness?.role);

  const handleDialogClose = async () => {
    setManageOpen(false);
    await refreshConnection();
  };

  if (!connection || connection.status !== 'active') {
    return (
      <>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <EnvelopeSimple size={48} weight="thin" />
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Connect your enquiries inbox</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 480, mx: 'auto' }}>
            Link the email address couples already use to reach you (for example your enquiries@ address).
            After a quick sign-in with Google or Microsoft, new messages show up here under the Email tab so
            your team can read and reply in one place. Replies always come from that same address. If something
            needs to live on a couple record, use Save as a note when you are in a thread.
          </Typography>
          {callbackBanner && (
            <Alert
              severity={callbackBanner.severity}
              onClose={() => setCallbackBanner(null)}
              sx={{ maxWidth: 460, mx: 'auto', mb: 2, textAlign: 'left' }}
            >
              {callbackBanner.text}
            </Alert>
          )}
          {canManage ? (
            <Button
              variant="contained"
              startIcon={<EnvelopeSimple size={18} />}
              onClick={() => setManageOpen(true)}
            >
              Connect inbox
            </Button>
          ) : (
            <Alert severity="info" sx={{ maxWidth: 460, mx: 'auto' }}>
              Ask your business owner or an admin to link your enquiries inbox. You will still be able to read and reply here once it is connected.
            </Alert>
          )}
        </Box>
        <MailConnectionSettings
          open={manageOpen}
          onClose={handleDialogClose}
          businessId={businessId}
          canManage={canManage}
        />
      </>
    );
  }

  const lastMessage = activeDetail?.messages?.[activeDetail.messages.length - 1];
  const defaultNoteContent = lastMessage?.bodyText || lastMessage?.snippet || '';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        height: { xs: 'calc(100vh - 220px)', md: 'calc(100vh - 160px)' },
        minHeight: { xs: 420, md: 600 },
        overflow: 'hidden'
      }}
    >
      {/* Threads list */}
      <Box
        sx={{
          width: { xs: '100%', md: 340 },
          flexShrink: 0,
          display: { xs: activeThreadId ? 'none' : 'flex', md: 'flex' },
          flexDirection: 'column',
          minWidth: 0,
          height: '100%'
        }}
      >
        <Box
          sx={{
            minHeight: HEADER_HEIGHT,
            px: 2.5,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <EnvelopeSimple size={20} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
              {connection.emailAddress}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => fetchThreads({ reset: true })} disabled={listLoading} aria-label="Refresh">
            <ArrowsClockwise size={18} />
          </IconButton>
          <IconButton size="small" onClick={() => setManageOpen(true)} aria-label="Manage inbox">
            <Gear size={18} />
          </IconButton>
        </Box>

        {callbackBanner && (
          <Alert severity={callbackBanner.severity} sx={{ m: 1 }} onClose={() => setCallbackBanner(null)}>
            {callbackBanner.text}
          </Alert>
        )}

        {listError && (
          <Alert severity="error" sx={{ m: 1 }}>{listError}</Alert>
        )}

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {listLoading && threads.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : threads.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No recent emails in the inbox.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {threads.map((thread) => (
                <React.Fragment key={thread.threadId}>
                  <ThreadListItem
                    thread={thread}
                    active={activeThreadId === thread.threadId}
                    onClick={() => handleSelectThread(thread.threadId)}
                  />
                </React.Fragment>
              ))}
              {nextPageToken && (
                <Box sx={{ p: 1, textAlign: 'center' }}>
                  <Button
                    size="small"
                    onClick={() => fetchThreads({ reset: false, pageToken: nextPageToken })}
                    disabled={loadingMore || listLoading}
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </Button>
                </Box>
              )}
            </List>
          )}
        </Box>
      </Box>

      {/* Thread detail */}
      <Box
        sx={{
          flex: 1,
          display: { xs: activeThreadId ? 'flex' : 'none', md: 'flex' },
          flexDirection: 'column',
          minWidth: 0,
          backgroundColor: '#fff',
          borderRadius: '16px',
          overflow: 'hidden',
          ml: { xs: 0, md: 2 },
          height: '100%'
        }}
      >
        {actionError && (
          <Alert severity="error" onClose={() => setActionError('')} sx={{ m: 1 }}>
            {actionError}
          </Alert>
        )}
        {actionSuccess && (
          <Alert severity="success" onClose={() => setActionSuccess('')} sx={{ m: 1 }}>
            {actionSuccess}
          </Alert>
        )}
        <ThreadDetail
          thread={activeThread}
          detail={activeDetail}
          loading={detailLoading}
          onReply={handleReply}
          sending={sending}
          checkingLeadMatch={checkingLeadMatch}
          onAddAsLead={handleAddAsLead}
          onSaveNote={() => setSaveNoteOpen(true)}
          matchedLead={matchedLead}
          connectionEmail={connection?.emailAddress}
          onViewLead={() => {
            if (!matchedLead?._id) return;
            navigate(`/supplier/crm/leads/${matchedLead._id}`);
          }}
        />
      </Box>

      <AddLeadDialog
        open={addLeadOpen}
        onClose={() => setAddLeadOpen(false)}
        onSubmit={handleSubmitAddLead}
        defaultEmail={extractEmail(activeThread?.from)}
        defaultName={extractName(activeThread?.from)}
        submitting={savingLead}
      />

      <SaveNoteDialog
        open={saveNoteOpen}
        onClose={() => setSaveNoteOpen(false)}
        onSubmit={handleSubmitSaveNote}
        defaultTitle={`Email: ${lastMessage?.subject || activeThread?.subject || ''}`.slice(0, 200)}
        defaultContent={defaultNoteContent}
        submitting={savingNote}
      />

      <MailConnectionSettings
        open={manageOpen}
        onClose={handleDialogClose}
        businessId={businessId}
        canManage={canManage}
      />
    </Box>
  );
};

export default EmailInbox;
